import React, { useState, useEffect, useRef } from 'react';
import { mayoristaAPI, clientesAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import TicketPrinter from '../components/TicketPrinter';
import { 
  Store, Search, Plus, Minus, Trash2, ShoppingCart, 
  User, Phone, FileText, X, Check, Package, DollarSign,
  Calendar, Eye, Printer
} from 'lucide-react';

const MayoristaPage = () => {
  const { user } = useAuth();
  const { formatCurrency, config } = useConfig();
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [carrito, setCarrito] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [vista, setVista] = useState('nueva'); // 'nueva' o 'historial'
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [showItemPersonalizado, setShowItemPersonalizado] = useState(false);
  
  const [clienteData, setClienteData] = useState({
    cliente_id: null,
    cliente_nombre: '',
    cliente_telefono: '',
    cliente_nit: ''
  });

  const [itemPersonalizado, setItemPersonalizado] = useState({
    nombre: '',
    cantidad: 1,
    precio_mayoreo: 0
  });

  const [formaPago, setFormaPago] = useState('efectivo');
  const [esCredito, setEsCredito] = useState(false);
  const [diasCredito, setDiasCredito] = useState(30);
  const [observaciones, setObservaciones] = useState('');

  const inputRef = useRef(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [productosRes, clientesRes, ventasRes] = await Promise.all([
        mayoristaAPI.getProductos(),
        clientesAPI.getAll().catch(() => ({ data: { success: false } })),
        mayoristaAPI.getAll()
      ]);
      
      if (productosRes.data.success) {
        const onlyMayorista = (productosRes.data.data || []).filter((p) => {
          const pm = Number.parseFloat(p?.precio_mayoreo ?? 0);
          return pm > 0;
        });
        setProductos(onlyMayorista);
      }
      
      if (clientesRes.data?.success) {
        setClientes(clientesRes.data.data || []);
      }
      
      if (ventasRes.data.success) {
        setVentas(ventasRes.data.data || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const productosFiltrados = productos.filter(p => 
    p.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.codigo_barras?.includes(busqueda) ||
    p.sku?.toLowerCase().includes(busqueda.toLowerCase())
  );

  const getStockBadge = (producto) => {
    const estado = producto?.stock_estado;
    // Usar 'stock' en lugar de 'stock_total' para consistencia
    const stock = Number.parseFloat(producto?.stock ?? producto?.stock_total ?? 0);

    if (estado === 'sin_stock' || stock <= 0) {
      return <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">Sin stock</span>;
    }
    if (estado === 'bajo') {
      return <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Stock bajo</span>;
    }
    return <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Disponible</span>;
  };

  const agregarAlCarrito = (producto) => {
    const existe = carrito.find(item => item.producto_id === producto.id);
    
    if (existe) {
      setCarrito(carrito.map(item => 
        item.producto_id === producto.id 
          ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * item.precio_mayoreo }
          : item
      ));
    } else {
      setCarrito([...carrito, {
        producto_id: producto.id,
        nombre: producto.nombre,
        codigo_barras: producto.codigo_barras || '',
        cantidad: 1,
        precio_unitario: parseFloat(producto.precio_venta),
        precio_mayoreo: parseFloat(producto.precio_mayoreo || producto.precio_venta),
        descuento: 0,
        descuento_monto: 0,
        subtotal: parseFloat(producto.precio_mayoreo || producto.precio_venta)
      }]);
    }
    
    setBusqueda('');
    inputRef.current?.focus();
  };

  const agregarItemPersonalizado = () => {
    if (!itemPersonalizado.nombre || itemPersonalizado.precio_mayoreo <= 0) {
      alert('Ingrese nombre y precio del producto');
      return;
    }

    setCarrito([...carrito, {
      producto_id: null,
      nombre: itemPersonalizado.nombre,
      nombre_producto: itemPersonalizado.nombre,
      codigo_barras: '',
      cantidad: parseFloat(itemPersonalizado.cantidad) || 1,
      precio_unitario: parseFloat(itemPersonalizado.precio_mayoreo),
      precio_mayoreo: parseFloat(itemPersonalizado.precio_mayoreo),
      descuento: 0,
      descuento_monto: 0,
      subtotal: (parseFloat(itemPersonalizado.cantidad) || 1) * parseFloat(itemPersonalizado.precio_mayoreo),
      es_personalizado: true
    }]);

    setItemPersonalizado({ nombre: '', cantidad: 1, precio_mayoreo: 0 });
    setShowItemPersonalizado(false);
  };

  const actualizarCantidad = (index, nuevaCantidad) => {
    if (nuevaCantidad < 1) return;
    
    setCarrito(carrito.map((item, i) => 
      i === index 
        ? { ...item, cantidad: nuevaCantidad, subtotal: nuevaCantidad * item.precio_mayoreo }
        : item
    ));
  };

  const actualizarPrecio = (index, nuevoPrecio) => {
    setCarrito(carrito.map((item, i) => 
      i === index 
        ? { ...item, precio_mayoreo: nuevoPrecio, subtotal: item.cantidad * nuevoPrecio }
        : item
    ));
  };

  const eliminarDelCarrito = (index) => {
    setCarrito(carrito.filter((_, i) => i !== index));
  };

  const vaciarCarrito = () => {
    if (carrito.length > 0 && window.confirm('¿Vaciar el carrito?')) {
      setCarrito([]);
    }
  };

  const calcularTotal = () => {
    return carrito.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const seleccionarCliente = (cliente) => {
    setClienteData({
      cliente_id: cliente.id,
      cliente_nombre: cliente.nombre,
      cliente_telefono: cliente.telefono || '',
      cliente_nit: cliente.nit || cliente.rfc || ''
    });
    setShowClienteModal(false);
  };

  const finalizarVenta = async () => {
    if (carrito.length === 0) {
      alert('El carrito está vacío');
      return;
    }

    if (!clienteData.cliente_nombre) {
      alert('Seleccione o ingrese un cliente');
      return;
    }

    try {
      setLoading(true);

      const ventaData = {
        cliente_id: clienteData.cliente_id,
        cliente_nombre: clienteData.cliente_nombre,
        cliente_telefono: clienteData.cliente_telefono,
        cliente_nit: clienteData.cliente_nit,
        vendedor_id: user?.id,
        usuario_nombre: user?.username || user?.nombre_completo,
        subtotal: calcularTotal(),
        descuento: 0,
        impuestos: 0,
        total: calcularTotal(),
        forma_pago: formaPago,
        metodo_pago: formaPago.charAt(0).toUpperCase() + formaPago.slice(1),
        es_credito: esCredito,
        dias_credito: diasCredito,
        observaciones: observaciones,
        detalle: carrito
      };

      const response = await mayoristaAPI.create(ventaData);

      if (response.data.success) {
        const ventaCreada = response.data.data;
        alert(`Venta mayorista creada exitosamente\nFolio: ${ventaCreada.folio}`);
        
        // Generar ticket
        const ventaCompleta = {
          ...ventaData,
          ...ventaCreada,
          tipo: 'mayorista', // Marcar como venta mayorista
          es_mayorista: true,
          detalle: carrito.map(item => ({
            ...item,
            producto_nombre: item.nombre,
            precio_unitario: item.precio_mayoreo
          }))
        };
        
        const printer = new TicketPrinter({
          nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
          direccion: config?.direccion || '',
          telefono: config?.telefono || '',
          rfc: config?.nit || '',
          mensaje: config?.mensaje_ticket || 'Gracias por su preferencia'
        });
        
        printer.print(ventaCompleta);
        
        // Limpiar
        setCarrito([]);
        setClienteData({ cliente_id: null, cliente_nombre: '', cliente_telefono: '', cliente_nit: '' });
        setObservaciones('');
        setEsCredito(false);
        setFormaPago('efectivo');
        
        // Recargar ventas
        cargarDatos();
      }
    } catch (error) {
      console.error('Error al crear venta:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const verDetalleVenta = async (venta) => {
    try {
      const response = await mayoristaAPI.getById(venta.id);
      if (response.data.success) {
        setVentaSeleccionada(response.data.data);
        setShowDetalleModal(true);
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  };

  const cancelarVenta = async (id) => {
    if (!window.confirm('¿Está seguro de cancelar esta venta mayorista?')) return;
    
    try {
      await mayoristaAPI.cancel(id);
      alert('Venta cancelada');
      cargarDatos();
    } catch (error) {
      console.error('Error al cancelar:', error);
      alert('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading && productos.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Store className="w-8 h-8 text-orange-600" />
              Ventas Mayorista
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Precios de mayoreo para clientes especiales
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setVista('nueva')}
              className={`px-4 py-2 rounded-lg ${vista === 'nueva' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Nueva Venta
            </button>
            <button
              onClick={() => setVista('historial')}
              className={`px-4 py-2 rounded-lg ${vista === 'historial' ? 'bg-orange-600 text-white' : 'bg-gray-200 text-gray-700'}`}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Historial
            </button>
          </div>
        </div>
      </div>

      {vista === 'nueva' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel de búsqueda y productos */}
          <div className="lg:col-span-2 space-y-4">
            {/* Búsqueda */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar producto por nombre, código o SKU..."
                    className="input-field pl-10"
                    autoFocus
                  />
                </div>
                <button
                  onClick={() => setShowItemPersonalizado(true)}
                  className="btn-secondary whitespace-nowrap"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Item Personalizado
                </button>
              </div>
              
              {/* Lista de productos filtrados */}
              {busqueda && (
                <div className="mt-4 max-h-64 overflow-y-auto border rounded-lg">
                  {productosFiltrados.length > 0 ? (
                    productosFiltrados.slice(0, 10).map(producto => (
                      <div
                        key={producto.id}
                        onClick={() => agregarAlCarrito(producto)}
                        className="p-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center"
                      >
                        <div>
                          <p className="font-medium">{producto.nombre}</p>
                          <p className="text-sm text-gray-500">
                            {producto.codigo_barras && `Código: ${producto.codigo_barras}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500 line-through">
                            {formatCurrency(producto.precio_venta)}
                          </p>
                          <p className="font-bold text-orange-600">
                            {formatCurrency(producto.precio_mayoreo || producto.precio_venta)}
                          </p>
                          <div className="mt-1">{getStockBadge(producto)}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="p-4 text-center text-gray-500">No se encontraron productos</p>
                  )}
                </div>
              )}
            </div>

            {/* Carrito */}
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-orange-600" />
                  Carrito ({carrito.length} items)
                </h2>
                {carrito.length > 0 && (
                  <button onClick={vaciarCarrito} className="text-red-600 text-sm">
                    <Trash2 className="w-4 h-4 inline mr-1" />
                    Vaciar
                  </button>
                )}
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {carrito.length > 0 ? (
                  <table className="w-full">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Cant.</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Precio</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Subtotal</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {carrito.map((item, index) => (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{item.nombre}</p>
                            {item.es_personalizado && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                                Personalizado
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button 
                                onClick={() => actualizarCantidad(index, item.cantidad - 1)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <input
                                type="number"
                                value={item.cantidad}
                                onChange={(e) => actualizarCantidad(index, parseInt(e.target.value) || 1)}
                                className="w-16 text-center border rounded p-1"
                                min="1"
                              />
                              <button 
                                onClick={() => actualizarCantidad(index, item.cantidad + 1)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              value={item.precio_mayoreo}
                              onChange={(e) => actualizarPrecio(index, parseFloat(e.target.value) || 0)}
                              className="w-24 text-right border rounded p-1"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {formatCurrency(item.subtotal)}
                          </td>
                          <td className="px-4 py-3">
                            <button 
                              onClick={() => eliminarDelCarrito(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>Carrito vacío</p>
                    <p className="text-sm">Busca productos para agregar</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Panel de cliente y pago */}
          <div className="space-y-4">
            {/* Cliente */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <User className="w-5 h-5 text-orange-600" />
                Cliente Mayorista
              </h3>
              
              <button
                onClick={() => setShowClienteModal(true)}
                className="w-full btn-secondary mb-3"
              >
                <Search className="w-4 h-4 mr-1" />
                Buscar Cliente
              </button>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={clienteData.cliente_nombre}
                    onChange={(e) => setClienteData({...clienteData, cliente_nombre: e.target.value})}
                    className="input-field"
                    placeholder="Nombre del cliente"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={clienteData.cliente_telefono}
                    onChange={(e) => setClienteData({...clienteData, cliente_telefono: e.target.value})}
                    className="input-field"
                    placeholder="Teléfono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NIT / RFC</label>
                  <input
                    type="text"
                    value={clienteData.cliente_nit}
                    onChange={(e) => setClienteData({...clienteData, cliente_nit: e.target.value})}
                    className="input-field"
                    placeholder="NIT o RFC"
                  />
                </div>
              </div>
            </div>

            {/* Forma de pago */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-600" />
                Forma de Pago
              </h3>
              
              <div className="space-y-3">
                <select
                  value={formaPago}
                  onChange={(e) => setFormaPago(e.target.value)}
                  className="input-field"
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="cheque">Cheque</option>
                  <option value="tarjeta">Tarjeta</option>
                </select>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={esCredito}
                    onChange={(e) => setEsCredito(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Venta a crédito</span>
                </label>

                {esCredito && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Días de crédito</label>
                    <input
                      type="number"
                      value={diasCredito}
                      onChange={(e) => setDiasCredito(parseInt(e.target.value) || 30)}
                      className="input-field"
                      min="1"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                  <textarea
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    className="input-field"
                    rows="2"
                    placeholder="Notas adicionales..."
                  />
                </div>
              </div>
            </div>

            {/* Total y finalizar */}
            <div className="bg-orange-600 rounded-lg shadow-md p-4 text-white">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg">Total:</span>
                <span className="text-3xl font-bold">{formatCurrency(calcularTotal())}</span>
              </div>
              
              <button
                onClick={finalizarVenta}
                disabled={loading || carrito.length === 0}
                className="w-full bg-white text-orange-600 font-bold py-3 rounded-lg hover:bg-orange-50 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {loading ? 'Procesando...' : 'Finalizar Venta'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Vista de historial */
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {ventas.map(venta => (
                  <tr key={venta.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium text-orange-600">{venta.folio}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(venta.fecha_venta).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{venta.cliente_nombre || venta.cliente_nombre_db || 'Sin cliente'}</p>
                    </td>
                    <td className="px-6 py-4 text-right font-bold">{formatCurrency(venta.total)}</td>
                    
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        venta.estado === 'completada' ? 'bg-green-100 text-green-800' :
                        venta.estado === 'cancelada' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {venta.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => verDetalleVenta(venta)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Ver detalle"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {venta.estado !== 'cancelada' && (
                          <button
                            onClick={() => cancelarVenta(venta.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Cancelar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {ventas.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <Store className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No hay ventas mayorista registradas</p>
            </div>
          )}
        </div>
      )}

      {/* Modal buscar cliente */}
      {showClienteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Buscar Cliente</h2>
              <button onClick={() => setShowClienteModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              {clientes.length > 0 ? (
                <div className="space-y-2">
                  {clientes.map(cliente => (
                    <div
                      key={cliente.id}
                      onClick={() => seleccionarCliente(cliente)}
                      className="p-3 border rounded-lg hover:bg-orange-50 cursor-pointer"
                    >
                      <p className="font-medium">{cliente.nombre}</p>
                      <p className="text-sm text-gray-500">{cliente.telefono}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No hay clientes registrados</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal item personalizado */}
      {showItemPersonalizado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Agregar Item Personalizado</h2>
              <button onClick={() => setShowItemPersonalizado(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del producto *</label>
                <input
                  type="text"
                  value={itemPersonalizado.nombre}
                  onChange={(e) => setItemPersonalizado({...itemPersonalizado, nombre: e.target.value})}
                  className="input-field"
                  placeholder="Descripción del producto"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
                  <input
                    type="number"
                    value={itemPersonalizado.cantidad}
                    onChange={(e) => setItemPersonalizado({...itemPersonalizado, cantidad: e.target.value})}
                    className="input-field"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio *</label>
                  <input
                    type="number"
                    value={itemPersonalizado.precio_mayoreo}
                    onChange={(e) => setItemPersonalizado({...itemPersonalizado, precio_mayoreo: e.target.value})}
                    className="input-field"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowItemPersonalizado(false)}
                  className="flex-1 btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={agregarItemPersonalizado}
                  className="flex-1 bg-orange-600 text-white rounded-lg py-2 hover:bg-orange-700"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle venta */}
      {showDetalleModal && ventaSeleccionada && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold">Detalle Venta {ventaSeleccionada.folio}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const ventaCompleta = {
                      ...ventaSeleccionada,
                      tipo: 'mayorista',
                      es_mayorista: true
                    };
                    const printer = new TicketPrinter({
                      nombreNegocio: config?.nombre_negocio || 'Mi Tienda',
                      direccion: config?.direccion || '',
                      telefono: config?.telefono || '',
                      rfc: config?.nit || '',
                      mensaje: config?.mensaje_ticket || 'Gracias por su preferencia'
                    });
                    printer.print(ventaCompleta);
                  }}
                  className="btn-secondary"
                  title="Imprimir ticket"
                >
                  <Printer className="w-5 h-5" />
                </button>
                <button onClick={() => setShowDetalleModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <p className="text-sm text-gray-500">Cliente</p>
                  <p className="font-medium">{ventaSeleccionada.cliente_nombre || 'Sin cliente'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha</p>
                  <p className="font-medium">{new Date(ventaSeleccionada.fecha_venta).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Forma de pago</p>
                  <p className="font-medium">{ventaSeleccionada.forma_pago}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Estado</p>
                  <p className="font-medium">{ventaSeleccionada.estado}</p>
                </div>
              </div>

              <table className="w-full border-t">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Cant.</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Precio</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {ventaSeleccionada.detalle?.map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="px-4 py-2">
                        {item.producto_nombre || item.nombre_producto}
                        {item.es_personalizado && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1 ml-1 rounded">Custom</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center">{item.cantidad}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(item.precio_mayoreo || item.precio_unitario)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-orange-50">
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right font-bold">Total:</td>
                    <td className="px-4 py-3 text-right font-bold text-lg">{formatCurrency(ventaSeleccionada.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MayoristaPage;
